
import { useRef, useEffect } from "react";
import { Loader } from "lucide-react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UseFormReturn } from "react-hook-form";
import { VenueFormValues } from "./types";

type VenueSearchProps = {
  form: UseFormReturn<VenueFormValues>;
  isGoogleMapsLoaded: boolean;
  isLoadingPlaces: boolean;
  setIsLoadingPlaces: (loading: boolean) => void;
};

export function VenueSearch({ 
  form, 
  isGoogleMapsLoaded, 
  isLoadingPlaces, 
  setIsLoadingPlaces 
}: VenueSearchProps) {
  const autocompleteRef = useRef<HTMLInputElement>(null);
  const autocompleteInstance = useRef<google.maps.places.Autocomplete | null>(null);

  // Initialize autocomplete
  useEffect(() => {
    if (!isGoogleMapsLoaded || !autocompleteRef.current) return;

    try {
      console.log("Initializing Places Autocomplete");
      autocompleteInstance.current = new google.maps.places.Autocomplete(autocompleteRef.current, {
        types: ["establishment"],
        fields: ["name", "formatted_address", "address_components", "geometry", "url", "formatted_phone_number", "opening_hours", "website"],
      });

      autocompleteInstance.current.addListener("place_changed", () => {
        setIsLoadingPlaces(true);
        const place = autocompleteInstance.current?.getPlace();
        
        if (!place || !place.geometry) {
          console.error("No details available for this place");
          toast.error("No details available for this place");
          setIsLoadingPlaces(false);
          return;
        }

        console.log("Place selected:", place);
        
        let streetNumber = "";
        let route = "";
        let city = "";
        let state = "";
        let zip = "";

        place.address_components?.forEach((component) => {
          const types = component.types;
          if (types.includes("street_number")) {
            streetNumber = component.long_name;
          } else if (types.includes("route")) {
            route = component.long_name;
          } else if (types.includes("locality")) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            state = component.short_name;
          } else if (types.includes("postal_code")) {
            zip = component.long_name;
          }
        });

        const address = `${streetNumber} ${route}`.trim();
        
        form.setValue("name", place.name || "");
        form.setValue("address", address || place.formatted_address || "");
        form.setValue("city", city);
        form.setValue("state", state);
        form.setValue("zip", zip);
        form.setValue("phone", place.formatted_phone_number || "");
        form.setValue("website", place.website || "");
        
        if (place.geometry.location) {
          form.setValue("latitude", place.geometry.location.lat());
          form.setValue("longitude", place.geometry.location.lng());
        }
        
        form.setValue("maps_link", place.url || "");
        
        if (place.opening_hours) {
          const hoursText = place.opening_hours.weekday_text?.join("\n") || "";
          form.setValue("hours", hoursText);
        }

        toast.success("Venue details loaded", {
          description: `Successfully loaded details for ${place.name}`
        });
        setIsLoadingPlaces(false);
      });

      console.log("Places Autocomplete initialized successfully");
    } catch (error) {
      console.error("Error initializing Places Autocomplete:", error);
      toast.error("Places API Error", {
        description: "Failed to initialize Google Places. Your API key may not have Places API enabled."
      });
    }

    return () => {
      if (autocompleteInstance.current) {
        google.maps.event.clearInstanceListeners(autocompleteInstance.current);
      }
    };
  }, [isGoogleMapsLoaded, form, setIsLoadingPlaces]);

  return (
    <div className="relative">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Search for a venue</FormLabel>
            <div className="relative">
              <FormControl>
                <Input
                  placeholder="Search for a venue or business..."
                  {...field}
                  ref={autocompleteRef}
                  disabled={!isGoogleMapsLoaded}
                />
              </FormControl>
              {isLoadingPlaces && (
                <div className="absolute right-3 top-2.5">
                  <Loader className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      {!isGoogleMapsLoaded && (
        <p className="text-sm text-muted-foreground mt-1">
          Loading Google Maps... Please wait
        </p>
      )}
    </div>
  );
}
